import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useData } from '@happyspace/sdk/data';
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Store,
  Send,
  Check,
  AlertCircle,
  ShoppingBag,
  Navigation,
} from 'lucide-react';
import PageHero from '../components/PageHero';
import { SITE, MAPS_QUERY } from '../lib/site';

type Status = 'idle' | 'sending' | 'sent' | 'error';

export default function Contact() {
  // Update page title and meta tags
  React.useEffect(() => {
    document.title = 'Contact Yalla Eat - Halal Food Plano, TX | Order & Visit';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Contact Yalla Eat in Plano, TX. Order halal shawarma and gyros online for pickup in ~20 minutes, or ask about catering. Visit us at 2380 E Park Blvd.');
  }, []);

  // Writes a row to the business's `contact_messages` table via the platform data API.
  const { insert } = useData('contact_messages');
  const [status, setStatus] = useState<Status>('idle');
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: 'General enquiry', message: '' });

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (status === 'sending') return;
    setStatus('sending');
    try {
      await insert({ ...form, submittedAt: new Date().toISOString() });
      setStatus('sent');
      setForm({ name: '', email: '', phone: '', subject: 'General enquiry', message: '' });
    } catch {
      setStatus('error');
    }
  }

  const DETAILS = [
    { icon: MapPin, label: 'Address', value: SITE.addressLines.join(', '), href: `https://www.google.com/maps/search/?api=1&query=${MAPS_QUERY}` },
    { icon: Phone, label: 'Phone', value: SITE.phone, href: SITE.phoneHref },
    { icon: Mail, label: 'Email', value: SITE.email, href: SITE.emailHref },
    { icon: Clock, label: 'Opening Hours', value: SITE.hours },
  ];

  return (
    <>
      <PageHero
        eyebrow="Contact Us"
        title="Get In"
        highlight="Touch"
        intro="Find us in Plano, Texas. Order ahead for pickup, ask about catering, or just tell us what you thought of your last shawarma."
      />

      {/* Details + form */}
      <section className="pt-6 md:pt-8 pb-12 md:pb-16 bg-white">
        <div className="max-w-screen-xl mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left: details */}
          <div>
            <span
              className="text-xs font-extrabold uppercase tracking-[0.25em]"
              style={{ color: 'var(--color-ink)' }}
            >
              Get In Touch
            </span>
            <h2 className="mt-3 display text-3xl md:text-4xl text-ink rule-gold">Visit The Kitchen</h2>

            <dl className="mt-8 space-y-6">
              {DETAILS.map((d) => (
                <div key={d.label} className="flex items-start gap-4">
                  <span
                    className="shrink-0 w-12 h-12 rounded-full border-2 flex items-center justify-center"
                    style={{ borderColor: 'var(--color-yellow)' }}
                  >
                    <d.icon size={19} style={{ color: 'var(--color-ink)' }} />
                  </span>
                  <div>
                    <dt className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-ink-2">
                      {d.label}
                    </dt>
                    <dd className="mt-1 text-[15px] font-semibold text-ink">
                      {d.href ? (
                        <a
                          href={d.href}
                          target={d.href.startsWith('http') ? '_blank' : undefined}
                          rel={d.href.startsWith('http') ? 'noreferrer' : undefined}
                          className="hover:underline"
                        >
                          {d.value}
                        </a>
                      ) : (
                        d.value
                      )}
                    </dd>
                  </div>
                </div>
              ))}
            </dl>

            {/* Pickup notice */}
            <div
              className="mt-8 rounded-lg border-2 p-5"
              style={{ borderColor: 'var(--color-yellow)', background: 'var(--color-cream)' }}
            >
              <p className="flex items-center gap-2 text-[13px] font-extrabold uppercase tracking-wide" style={{ color: 'var(--color-ink)' }}>
                <Store size={16} /> Pickup Ordering
              </p>
              <p className="mt-2 text-[14px] text-ink-2 leading-relaxed">
                Order online and collect in about {SITE.pickupMinutes} minutes. We are not running
                delivery at the moment, everything is made fresh for pickup.
              </p>
              <Link to="/menu" className="btn-green mt-4">
                <ShoppingBag size={15} /> Order for Pickup
              </Link>
            </div>
          </div>

          {/* Right: form */}
          <div>
            <span
              className="text-xs font-extrabold uppercase tracking-[0.25em]"
              style={{ color: 'var(--color-ink)' }}
            >
              Send a Message
            </span>
            <h2 className="mt-3 display text-3xl md:text-4xl text-ink rule-gold">We&apos;re Here To Help</h2>

            {status === 'sent' ? (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-8 rounded-lg border-2 p-8 text-center"
                style={{ borderColor: 'var(--color-yellow)', background: 'var(--color-cream)' }}
              >
                <span
                  className="inline-flex w-14 h-14 rounded-full items-center justify-center"
                  style={{ background: 'var(--color-yellow)' }}
                >
                  <Check size={26} style={{ color: 'var(--color-ink)' }} />
                </span>
                <h3 className="mt-4 display text-2xl text-ink">Message Sent</h3>
                <p className="mt-2 text-[14px] text-ink-2">
                  Thanks for reaching out, we&apos;ll get back to you as soon as we can.
                </p>
                <button onClick={() => setStatus('idle')} className="btn-outline mt-5">
                  Send Another
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="mt-8 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Your Name" required>
                    <input
                      required
                      value={form.name}
                      onChange={set('name')}
                      className="hs-input"
                      placeholder="Full name"
                    />
                  </Field>
                  <Field label="Phone">
                    <input
                      value={form.phone}
                      onChange={set('phone')}
                      className="hs-input"
                      placeholder="Best number to reach you"
                    />
                  </Field>
                </div>

                <Field label="Email" required>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={set('email')}
                    className="hs-input"
                    placeholder="you@example.com"
                  />
                </Field>

                <Field label="Subject">
                  <select value={form.subject} onChange={set('subject')} className="hs-input">
                    <option>General enquiry</option>
                    <option>Catering enquiry</option>
                    <option>Feedback about my order</option>
                    <option>Something else</option>
                  </select>
                </Field>

                <Field label="Message" required>
                  <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={set('message')}
                    className="hs-input resize-y"
                    placeholder="How can we help?"
                  />
                </Field>

                {status === 'error' && (
                  <p className="flex items-start gap-2 rounded-md p-3 text-[13px]" style={{ background: '#fdecec', color: '#a12222' }}>
                    <AlertCircle size={15} className="mt-px shrink-0" />
                    Something went wrong sending that. Please try again, or give us a call.
                  </p>
                )}

                <button type="submit" disabled={status === 'sending'} className="btn-green w-full disabled:opacity-60">
                  {status === 'sending' ? 'Sending…' : (<><Send size={15} /> Send Message</>)}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Map */}
      <section style={{ background: 'var(--color-cream)' }}>
        <div className="max-w-screen-xl mx-auto px-5 md:px-8 py-12 md:py-14">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <span
                className="text-xs font-extrabold uppercase tracking-[0.25em]"
                style={{ color: 'var(--color-ink)' }}
              >
                Find Us
              </span>
              <h2 className="mt-3 display text-3xl md:text-4xl text-ink">{SITE.addressLines[0]}</h2>
              <p className="mt-2 text-[14px] text-ink-2">{SITE.addressLines[1]}</p>
            </div>
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${MAPS_QUERY}`}
              target="_blank"
              rel="noreferrer"
              className="btn-yellow"
            >
              <Navigation size={15} /> Get Directions
            </a>
          </div>

          <div
            className="mt-8 overflow-hidden rounded-lg border-2"
            style={{ borderColor: 'var(--color-line)' }}
          >
            <iframe
              title={`Map showing ${SITE.name} at ${SITE.address}`}
              src={`https://maps.google.com/maps?q=${MAPS_QUERY}&z=15&output=embed`}
              className="w-full h-[380px] block"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-ink-2">
        {label} {required && <span style={{ color: 'var(--color-ink)' }}>*</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
